"use client";

import { useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { HeroBackground } from "@/components/home/hero/HeroBackground";
import { HeroCTAButtons } from "@/components/home/hero/HeroCTAButtons";
import { HeroHeading } from "@/components/home/hero/HeroHeading";
import { HeroParticles } from "@/components/home/hero/HeroParticles";
import { HeroScrollIndicator } from "@/components/home/hero/HeroScrollIndicator";
import { HeroStatsCards } from "@/components/home/hero/HeroStatsCards";
import { EASE_SMOOTH } from "@/components/motion/constants";
import { TextScramble } from "@/components/motion/TextScramble";
import { site } from "@/content/site";
import { mergeHomeHero } from "@/lib/site-content-fallbacks";
import { useHeroParallax } from "@/hooks/useHeroParallax";
import type { HomeHeroContent, Stats } from "@/lib/api-types";

interface HomeCharityHeroProps {
  stats?: Stats;
  hero?: HomeHeroContent;
}

export function HomeCharityHero({ stats, hero }: HomeCharityHeroProps) {
  const reduced = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useHeroParallax(
    { sectionRef, backgroundRef, contentRef },
    reduced
  );

  const heroContent = mergeHomeHero(hero);

  return (
    <section
      ref={sectionRef}
      id="home"
      aria-label="Hero"
      className="relative flex min-h-screen flex-col justify-center overflow-hidden"
    >
      <HeroBackground
        ref={backgroundRef}
        src={heroContent.heroImage}
        alt={heroContent.heroImageAlt}
        kenBurns={!reduced}
        objectPosition="70% center"
        overlay="minimal"
      />

      <HeroParticles enabled={!reduced} />

      <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col items-stretch gap-4 px-4 pb-24 pt-24 md:gap-5 md:px-6 md:pb-28 md:pt-28">
        <div ref={contentRef} className="max-w-2xl">
          <motion.div
            className="relative"
            initial={reduced ? false : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE_SMOOTH, delay: 0.05 }}
          >
            <div
              className="hero-text-scrim pointer-events-none absolute -inset-x-8 -inset-y-6 rounded-3xl"
              aria-hidden
            />

            <div className="relative">
              <TextScramble
                text={heroContent.eyebrow}
                as="p"
                className="mb-3 text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-[var(--hero-eyebrow)] hero-headline-shadow sm:text-xs"
                trigger="mount"
                delay={0.05}
                duration={0.7}
              />

              <HeroHeading lines={heroContent.headingLines} variant="onPhoto" />

              <motion.p
                className="mt-4 max-w-xl text-sm leading-relaxed text-white/92 drop-shadow sm:text-base"
                initial={reduced ? false : { opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.75,
                  ease: EASE_SMOOTH,
                  delay: reduced ? 0 : 0.32,
                }}
              >
                {heroContent.subtitle}
              </motion.p>

              <div className="mt-5">
                <HeroCTAButtons
                  primary={heroContent.primaryCta}
                  secondary={heroContent.secondaryCta}
                />
              </div>

              <motion.p
                className="mt-3 max-w-lg text-[0.65rem] leading-relaxed text-white/65 drop-shadow-sm sm:text-xs"
                initial={reduced ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: reduced ? 0 : 0.65 }}
              >
                {site.donate.taxBenefitNote}
              </motion.p>
            </div>
          </motion.div>
        </div>

        <div className="mt-8 md:mt-12 lg:mt-14">
          <HeroStatsCards stats={stats} />
        </div>
      </div>

      <HeroScrollIndicator />
    </section>
  );
}
